/**
 * RFC 0036 + RFC 0392: Agent chat synchronization hook.
 *
 * Manages conversation with the backend orchestrator via:
 * - WebSocket /ws/chat/{projectId} for real-time events (state, messages, chunks)
 * - POST /agent/connect to start a conversation (returns JSON)
 * - POST /agent/respond to answer questions (returns JSON)
 *
 * The backend runs agents as background tasks. Real-time events flow
 * through the persistent WebSocket connection.
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { getConfiguredApiBase, getAuthToken, api } from '../api/client'
import { useChatSocket, ChatEvent } from './useChatSocket'
import { createDebug, createNadDebug } from '../utils/debugLog'

const log = createDebug('dw:chat:agent')
const eventLog = createDebug('dw:chat:event')
const thinkingLog = createDebug('dw:chat:thinking')
const dedupLog = createNadDebug('dw:chat:dedup')

type ConversationStatus = 'idle' | 'running' | 'pending_questions' | 'error'

/** Answer value sent to the API: always has a selected option, optionally custom text */
export interface AnswerValue {
  selected_option: string
  custom_text?: string
}

/** Question embedded in a message (new unified model) */
export interface EmbeddedQuestion {
  question_type: string
  tool_use_id: string
  question: string
  options: Array<{ value?: string; label: string; description?: string }>
  allow_custom_input?: boolean
  header?: string
  answer?: string | null
  metadata?: Record<string, unknown> // Extra data (e.g., node details for cost confirmation)
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  agent?: string
  content: string
  referenced_node_ids?: string[]
  timestamp: string
  questions?: EmbeddedQuestion[] // Optional embedded questions
  /** True while this message is actively being streamed from the backend */
  isStreaming?: boolean
  /** True if this message was delivered via real streaming (not history) */
  wasStreamed?: boolean
  /** True for ephemeral system notifications (e.g. context compaction) */
  isSystemNotification?: boolean
}

interface UseAgentChatOptions {
  /** Visual style for generated assets (e.g., 'cinematic', 'anime') */
  style?: string
  /** Production target ('webtoon' or 'video') */
  productionTarget?: string
}

export function useAgentChat(projectId: string, options?: UseAgentChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [status, setStatus] = useState<ConversationStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRestoredSession, setIsRestoredSession] = useState(true)
  // RFC-0289: True between calling startWithPrompt and receiving the first
  // streaming chunk.  Used by AgentChat to show "Thinking..." correctly for
  // ALL paths that invoke the Director (send, edit-message, queued prompts).
  const [awaitingDirector, setAwaitingDirector] = useState(false)

  const firstUnansweredMessageIndex = useMemo(() => {
    const NON_GATING_IDS = new Set(['__cost_confirmation__', '__insufficient_funds__'])
    for (let i = 0; i < messages.length; i++) {
      const questions = messages[i].questions
      if (questions && questions.some(q => !q.answer && !NON_GATING_IDS.has(q.tool_use_id))) {
        return i
      }
    }
    return -1
  }, [messages])

  const styleRef = useRef(options?.style)
  const productionTargetRef = useRef(options?.productionTarget)

  useEffect(() => {
    styleRef.current = options?.style
  }, [options?.style])
  useEffect(() => {
    productionTargetRef.current = options?.productionTarget
  }, [options?.productionTarget])

  // Handle incoming WebSocket events
  const handleChatEvent = useCallback((event: ChatEvent) => {
    log('event type=%s', event.type)
    eventLog('received type=%s status=%s msgCount=%s', event.type, event.status ?? '-', event.messages ? (event.messages as unknown[]).length : '-')
    switch (event.type) {
      case 'state': {
        const incoming = (event.messages as ChatMessage[]) || []
        thinkingLog('state event status=%s msgCount=%d', event.status, incoming.length)
        eventLog('state status=%s incomingMsgCount=%d', event.status, incoming.length)
        setMessages((prev) => {
          // Preserve frontend-only `wasStreamed` flag so the Typewriter
          // doesn't re-animate messages that were already streamed live.
          const streamedTs = new Set(
            prev.filter((m) => m.wasStreamed).map((m) => m.timestamp),
          )

          // RFC-0289: Preserve optimistic user messages not yet in the
          // server's history.  When the user sends a message while workers
          // are running, the backend may broadcast a state event (from node
          // completion) before the user's POST is processed.  Without this,
          // the optimistic message is silently wiped.
          //
          // Content-based dedup: also filter out optimistic messages whose
          // content already appears in the incoming server messages. This
          // handles the common case where client and server timestamps differ
          // (client generates its own timestamp, server generates another),
          // which would otherwise cause the optimistic to always be preserved
          // alongside the server's copy — resulting in a duplicate.
          //
          // Count-aware: if the user sent the same text N times (e.g. "yes")
          // and the server has M copies, preserve max(0, N - M) optimistic
          // copies so repeated prompts aren't incorrectly dropped.
          const incomingTs = new Set(incoming.map((m) => m.timestamp))

          // Count how many times each content appears in incoming user messages
          const incomingContentCounts = new Map<string, number>()
          for (const m of incoming) {
            if (m.role === 'user') {
              incomingContentCounts.set(m.content, (incomingContentCounts.get(m.content) ?? 0) + 1)
            }
          }

          // Track how many optimistic messages we've matched per content
          const matchedCounts = new Map<string, number>()
          const optimistic = prev.filter((m) => {
            if (m.role !== 'user' || !m.id?.startsWith('user-')) return false
            // Keep if timestamp already matches (server has this exact message)
            if (incomingTs.has(m.timestamp)) return false

            // Content-based dedup: check if this content exists in incoming
            const serverCount = incomingContentCounts.get(m.content) ?? 0
            if (serverCount > 0) {
              const alreadyMatched = matchedCounts.get(m.content) ?? 0
              if (alreadyMatched < serverCount) {
                // This optimistic is accounted for by a server copy — drop it
                matchedCounts.set(m.content, alreadyMatched + 1)
                return false
              }
              // More optimistic copies than server copies — preserve the excess
            }
            return true
          })

          dedupLog({
            event: 'state-dedup',
            prev: prev.length,
            incoming: incoming.length,
            optimistic: optimistic.length,
            incomingTsCount: incomingTs.size,
            prevUserCount: prev.filter((m) => m.role === 'user').length,
          })

          // Preserve messages that are actively being streamed. During
          // streaming, the message isn't in the server's conversation
          // history yet, so a state event (e.g. from a node completing)
          // would wipe it.  When chat_stream_end + chat_message arrive
          // later, the streaming message is replaced by the final version.
          const streaming = prev.filter((m) => m.isStreaming)

          let merged = streamedTs.size === 0
            ? incoming
            : incoming.map((m) =>
                streamedTs.has(m.timestamp) ? { ...m, wasStreamed: true } : m,
              )

          // Append optimistic messages that the server doesn't know about yet
          if (optimistic.length > 0) {
            merged = [...merged, ...optimistic]
          }

          // Append actively streaming messages so they aren't wiped
          if (streaming.length > 0) {
            merged = [...merged, ...streaming]
          }

          return merged
        })
        const eventStatus = (event.status as ConversationStatus) || 'idle'
        setStatus(eventStatus)
        if (eventStatus !== 'error') {
          setError(null)
        }
        setIsLoading(false)
        break
      }

      case 'workflow':
        if (event.error) {
          setError(`Canvas error: ${event.error}`)
        }
        break

      case 'chat_message':
        setIsRestoredSession(false)
        setMessages((prev) => {
          if (event.role === 'user') {
            // Primary: backward scan for adjacent optimistic user message
            for (let i = prev.length - 1; i >= 0; i--) {
              const m = prev[i]
              if (
                m.role === 'user' &&
                m.id &&
                (m.id.startsWith('optimistic-answer-') || m.id.startsWith('user-'))
              ) {
                const updated = [...prev]
                updated[i] = event as unknown as ChatMessage
                return updated
              }
              if (m.role !== 'user') break
            }
            // Fallback: scan ALL messages for any optimistic with matching
            // content. This handles the case where an assistant message (e.g.
            // streaming chunk) arrived between the optimistic and the end of
            // the array, causing the backward scan to break early.
            const eventContent = (event as unknown as ChatMessage).content
            const optimisticIdx = prev.findIndex(
              (m) =>
                m.role === 'user' &&
                m.id?.startsWith('user-') &&
                m.content === eventContent,
            )
            if (optimisticIdx >= 0) {
              dedupLog({ event: 'chat-message-fallback', optimisticIdx: optimisticIdx })
              const updated = [...prev]
              updated[optimisticIdx] = event as unknown as ChatMessage
              return updated
            }
          }

          if (event.role === 'assistant' && (event.agent === 'director' || !event.agent)) {
            for (let i = prev.length - 1; i >= 0; i--) {
              const m = prev[i]
              if (m.role === 'assistant' && m.wasStreamed && m.id?.startsWith('stream-')) {
                const updated = [...prev]
                updated[i] = { ...(event as unknown as ChatMessage), wasStreamed: true }
                return updated
              }
              if (m.role !== 'assistant') break
            }
          }

          const existingIndex = prev.findIndex((m) => m.timestamp === (event as unknown as ChatMessage).timestamp)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = event as unknown as ChatMessage
            return updated
          }
          return [...prev, event as unknown as ChatMessage]
        })
        break

      case 'chat_chunk':
        setIsRestoredSession(false)
        log('chunk msgId=%s len=%d', event.message_id, ((event.content as string) || '').length)
        setMessages((prev) => {
          const msgId = event.message_id as string
          const chunk = (event.content as string) || ''
          const existingIndex = prev.findIndex((m) => m.id === msgId)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              content: updated[existingIndex].content + chunk,
            }
            return updated
          }
          return [
            ...prev,
            {
              id: msgId,
              role: 'assistant' as const,
              agent: 'director',
              content: chunk,
              timestamp: new Date().toISOString(),
              isStreaming: true,
              wasStreamed: true,
            },
          ]
        })
        break

      case 'chat_stream_end':
        log('stream_end msgId=%s has_tool_use=%s', event.message_id, event.has_tool_use)
        thinkingLog('chat_stream_end -> isStreaming=false for msgId=%s has_tool_use=%s',
          event.message_id, event.has_tool_use)
        setMessages((prev) => {
          const msgId = event.message_id as string
          const existingIndex = prev.findIndex((m) => m.id === msgId)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              isStreaming: false,
            }
            return updated
          }
          return prev
        })
        // Only clear awaitingDirector when the LLM round has no tool_use,
        // meaning this is the final round. If has_tool_use is true, the
        // Director will execute tools and make another LLM call, so keep
        // awaitingDirector=true to prevent the banner from flickering off.
        if (!event.has_tool_use) {
          thinkingLog('chat_stream_end -> no tool_use, clearing awaitingDirector')
          setAwaitingDirector(false)
        } else {
          thinkingLog('chat_stream_end -> has tool_use, keeping awaitingDirector=true')
        }
        break

      case 'status': {
        const newStatus = (event.status as ConversationStatus) || status
        thinkingLog('status event newStatus=%s (prev=%s)', newStatus, status)
        eventLog('status-change old=%s new=%s', status, newStatus)
        setStatus(newStatus)
        break
      }

      case 'done': {
        const doneStatus = (event.status as ConversationStatus) || 'idle'
        thinkingLog('done event doneStatus=%s -> awaitingDirector=false', doneStatus)
        eventLog('done status=%s', doneStatus)
        setStatus(doneStatus)
        setAwaitingDirector(false)
        break
      }

      case 'error':
        thinkingLog('error event -> awaitingDirector=false, msg=%s', event.message)
        eventLog('error msg=%s', event.message)
        setError((event.message as string) || 'Unknown error')
        setStatus('error')
        setAwaitingDirector(false)
        break

      case 'cancelled':
        thinkingLog('cancelled event -> awaitingDirector=false')
        eventLog('cancelled')
        setStatus('idle')
        setAwaitingDirector(false)
        setMessages((prev) => [
          ...prev,
          {
            id: `cancelled-${Date.now()}`,
            role: 'assistant',
            content: (event.message as string) || 'Execution stopped.',
            timestamp: new Date().toISOString(),
          },
        ])
        break

      case 'context_compacted':
        setMessages((prev) => [
          ...prev,
          {
            id: `compacted-${Date.now()}`,
            role: 'assistant',
            content: 'Compacting conversation history\u2026',
            timestamp: new Date().toISOString(),
            isSystemNotification: true,
          },
        ])
        break

      case 'node_status':
        // Cross-instance node status sync: when the node ran on a different
        // API instance, the YDoc write was local to that instance.  This
        // event arrives via Redis Pub/Sub so we can update the local YDoc.
        // Dispatch a DOM event so useYCanvas can apply it without prop threading.
        if (event.node_id && event.status) {
          window.dispatchEvent(
            new CustomEvent('dw:node-status', {
              detail: { nodeId: event.node_id, status: event.status },
            }),
          )
        }
        break
    }
  }, [status])

  // Connect WebSocket for real-time events
  useChatSocket({
    projectId,
    enabled: !!projectId,
    onEvent: handleChatEvent,
  })

  // Start a new conversation with a prompt (via POST /agent/connect)
  const startWithPrompt = useCallback(
    async (prompt: string, referencedNodeIds?: string[]) => {
      // RFC-0289: Backend accepts prompts during "running" state (workers
      // in-progress) so we no longer block here.  The Director lock on the
      // backend serialises concurrent invocations.
      if (!projectId) return

      // Show user's prompt in chat immediately (optimistic)
      if (prompt.trim()) {
        setMessages((prev) => [
          ...prev,
          {
            id: `user-${Date.now()}`,
            role: 'user',
            content: prompt,
            referenced_node_ids:
              referencedNodeIds && referencedNodeIds.length > 0 ? referencedNodeIds : undefined,
            timestamp: new Date().toISOString(),
          },
        ])
      }

      setStatus('running')
      setAwaitingDirector(true)
      thinkingLog('startWithPrompt -> status=running, awaitingDirector=true')
      setError(null)

      try {
        const apiBase = getConfiguredApiBase()
        const authToken = await getAuthToken()
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        }
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`
        }

        const body: Record<string, unknown> = {
          prompt,
          style: styleRef.current,
          production_target: productionTargetRef.current,
        }
        if (referencedNodeIds && referencedNodeIds.length > 0) {
          body.referenced_node_ids = referencedNodeIds
        }

        const response = await fetch(
          `${apiBase}/projects/${projectId}/agentic/agent/connect`,
          {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(body),
          }
        )

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }

        // JSON response — events come via WebSocket.
        // Log if the backend didn't actually spawn the Director (prompt silently ignored).
        const data = await response.json()
        if (prompt.trim() && data && !data.ok) {
          console.warn('[useAgentChat] /agent/connect did not spawn Director:', data.status)
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        console.error('Failed to start agent:', err)
        setError(err instanceof Error ? err.message : 'Failed to start agent')
        setStatus('idle')
      }
    },
    [projectId]
  )

  // Respond to pending questions (via POST /agent/respond)
  const respondToQuestions = useCallback(
    async (answers: Record<string, AnswerValue>) => {
      if (!projectId) {
        return
      }

      // Optimistically update question answers
      setMessages((prev) => {
        const updated = prev.map((msg) => {
          if (!msg.questions) return msg
          const updatedQuestions = msg.questions.map((q) => {
            const key = q.tool_use_id
            const answerVal = key ? answers[key] : undefined
            if (answerVal) {
              return { ...q, answer: answerVal.custom_text || answerVal.selected_option }
            }
            return q
          })
          if (updatedQuestions.some((q, i) => q !== msg.questions![i])) {
            return { ...msg, questions: updatedQuestions }
          }
          return msg
        })

        // Custom input still needs an optimistic user bubble
        if (answers['__custom_input__']?.custom_text) {
          updated.push({
            id: `user-${Date.now()}`,
            role: 'user',
            content: answers['__custom_input__'].custom_text,
            timestamp: new Date().toISOString(),
          })
        }
        return updated
      })

      setStatus('running')
      // Only set awaitingDirector when the answer resumes the Director
      // (i.e. not for scheduling-only questions like cost confirmation
      // or insufficient funds which only trigger worker nodes).
      const SCHEDULING_QUESTION_IDS = new Set(['__cost_confirmation__', '__insufficient_funds__'])
      const isDirectorAnswer = Object.keys(answers).some(
        (key) => !SCHEDULING_QUESTION_IDS.has(key),
      )
      if (isDirectorAnswer) {
        setAwaitingDirector(true)
        thinkingLog('respondToQuestions -> status=running, awaitingDirector=true (director answer)')
      } else {
        thinkingLog('respondToQuestions -> status=running (scheduling answer, awaitingDirector unchanged)')
      }
      setError(null)

      try {
        const apiBase = getConfiguredApiBase()
        const authToken = await getAuthToken()
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        }
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`
        }
        const response = await fetch(
          `${apiBase}/projects/${projectId}/agentic/agent/respond`,
          {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({ answers }),
          }
        )

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }

        // JSON response — events come via WebSocket
        await response.json()
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        console.error('Failed to respond to agent:', err)
        setError(err instanceof Error ? err.message : 'Failed to respond')
        setStatus('idle')
      }
    },
    [projectId]
  )

  // RFC 0061: Stop the running agent execution
  const stop = useCallback(async () => {
    if (!projectId || status !== 'running') return

    try {
      await api.cancelAgentExecution(projectId)
    } catch (err) {
      console.error('Failed to cancel agent:', err)
    }

    setStatus('idle')
  }, [projectId, status])

  // Delete a message by timestamp (optimistic removal + API call)
  const deleteMessage = useCallback(async (timestamp: string, content: string) => {
    if (!projectId || status === 'running') return

    setMessages((prev) => prev.filter((m) => m.timestamp !== timestamp))

    try {
      const apiBase = getConfiguredApiBase()
      const authToken = await getAuthToken()
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`
      }
      const response = await fetch(
        `${apiBase}/projects/${projectId}/agentic/agent/delete-message`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ timestamp, content }),
        }
      )
      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`)
      }
      const result = await response.json()
      if (!result.success) {
        console.warn('Delete message API returned:', result.reason)
      }
    } catch (err) {
      console.error('Failed to delete message:', err)
    }
  }, [projectId, status])

  // Edit a message: truncate from it onwards, then resend with new content
  const editMessage = useCallback(async (timestamp: string, originalContent: string, newContent: string, referencedNodeIds?: string[]) => {
    if (!projectId || status === 'running') return

    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.timestamp === timestamp)
      if (idx < 0) return prev
      return prev.slice(0, idx)
    })

    try {
      const apiBase = getConfiguredApiBase()
      const authToken = await getAuthToken()
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`
      }
      const response = await fetch(
        `${apiBase}/projects/${projectId}/agentic/agent/edit-message`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ timestamp, content: originalContent }),
        }
      )
      if (!response.ok) {
        throw new Error(`Edit failed: ${response.status}`)
      }
      const result = await response.json()
      if (!result.success) {
        console.warn('Edit message API returned:', result.reason)
        return
      }
    } catch (err) {
      console.error('Failed to edit message:', err)
      return
    }

    await startWithPrompt(newContent, referencedNodeIds)
  }, [projectId, status, startWithPrompt])

  const dismissPendingQuestions = useCallback(() => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (!msg.questions) return msg
        const updated = msg.questions.map((q) =>
          q.answer ? q : { ...q, answer: '__skipped__' }
        )
        if (updated.some((q, i) => q !== msg.questions![i])) {
          return { ...msg, questions: updated }
        }
        return msg
      }),
    )
  }, [])

  return {
    messages,
    firstUnansweredMessageIndex,
    status,
    error,
    isLoading,
    isRestoredSession,
    awaitingDirector,
    startWithPrompt,
    respondToQuestions,
    dismissPendingQuestions,
    deleteMessage,
    editMessage,
    stop,
  }
}
